import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { games, genres } from "@/data/games";
import { useOnsiteAvailability } from "@/hooks/useOnsiteAvailability";
import playfieldLogo from "@/assets/playfield-logo.png";

const GamesOnsite = () => {
  const navigate = useNavigate();
  const { isDisabled, toggleGame } = useOnsiteAvailability();
  const [selectedGenre, setSelectedGenre] = useState<string>("All Games");

  useEffect(() => {
    document.title = "Onsite Mode — PlayfieldVR";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const filteredGames = (
    selectedGenre === "All Games"
      ? games
      : games.filter((g) => g.genres.includes(selectedGenre))
  ).sort((a, b) => {
    const aDisabled = Number(isDisabled(a.slug));
    const bDisabled = Number(isDisabled(b.slug));
    if (aDisabled !== bDisabled) return aDisabled - bDisabled;
    return a.title.localeCompare(b.title);
  });

  return (
    <div className="min-h-screen bg-background">
      <section className="py-10 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col items-center gap-8 mb-10">
            <img
              src={playfieldLogo}
              alt="PlayfieldVR"
              className="h-16 w-auto"
            />
            <div className="flex flex-wrap items-center justify-center gap-2">
              {genres.map((genre) => (
                <Button
                  key={genre}
                  variant={selectedGenre === genre ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGenre(genre)}
                  className="rounded-full"
                >
                  {genre}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredGames.map((game) => {
              const off = isDisabled(game.slug);
              return (
                <Card
                  key={game.slug}
                  onClick={() => navigate(`/experiences/${game.slug}`)}
                  className={`group relative cursor-pointer overflow-hidden border-border/50 bg-card transition-all duration-300 hover:-translate-y-2 hover:shadow-lg ${
                    off ? "opacity-40 grayscale" : ""
                  }`}
                >
                  <div className="relative h-64 overflow-hidden bg-card isolate [contain:paint]">
                    <div
                      role="img"
                      aria-label={game.title}
                      className="absolute inset-0 bg-cover bg-no-repeat transition-transform duration-500 ease-out group-hover:scale-110"
                      style={{
                        backgroundImage: `url(${game.image})`,
                        backgroundPosition: "center top",
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                    {off && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                        <span className="px-4 py-2 rounded-md bg-destructive text-destructive-foreground font-bold text-lg uppercase tracking-wider">
                          Niet beschikbaar
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="relative z-10 p-5 bg-card">
                    <h3 className="text-2xl font-semibold tracking-tight">{game.title}</h3>
                  </div>

                  {/* Discrete onsite toggle — does not navigate */}
                  <div
                    className="absolute top-3 right-3 z-20 flex items-center gap-2 rounded-full bg-background/80 backdrop-blur-sm px-2 py-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Switch
                      checked={!off}
                      onCheckedChange={() => toggleGame(game.slug)}
                      aria-label={`${game.title} ${off ? "inschakelen" : "uitschakelen"}`}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GamesOnsite;
